import { useEffect, useCallback } from 'react';
import type { ShortcutMap, ShortcutOptions } from '../types';

/**
 * Hook for handling keyboard shortcuts
 * @param shortcuts - Map of key combinations to handlers
 * @param options - Options { enabled: boolean }
 *
 * @example
 * useKeyboardShortcuts({
 *   'ctrl+k': () => openSearch(),
 *   'escape': () => closeModal(),
 *   'ctrl+s': (e) => { e.preventDefault(); save(); }
 * });
 */
export function useKeyboardShortcuts(shortcuts: ShortcutMap, options: ShortcutOptions = {}): void {
  const { enabled = true } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      const isContentEditable = target.isContentEditable;

      // Build the key combination string
      const parts: string[] = [];
      if (event.ctrlKey || event.metaKey) parts.push('ctrl');
      if (event.altKey) parts.push('alt');
      if (event.shiftKey) parts.push('shift');

      // Normalize key name
      let key = event.key.toLowerCase();
      if (key === ' ') key = 'space';
      if (key === 'escape') key = 'escape';

      // Don't add modifier keys as the main key
      if (!['control', 'alt', 'shift', 'meta'].includes(key)) {
        parts.push(key);
      }

      const combination = parts.join('+');

      // Check for matching shortcut
      const handler = shortcuts[combination];
      if (handler) {
        // Allow escape to work in inputs
        if (combination === 'escape' || (!isInput && !isContentEditable)) {
          handler(event);
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

interface ModalShortcutOptions {
  onClose?: () => void;
  enabled?: boolean;
}

/**
 * Common keyboard shortcuts for modals
 * @param options - { onClose: function, enabled: boolean }
 */
export function useModalKeyboardShortcuts({ onClose, enabled = true }: ModalShortcutOptions): void {
  useKeyboardShortcuts(
    {
      escape: () => onClose?.(),
    },
    { enabled }
  );
}

/**
 * Command palette shortcut (Ctrl/Cmd + K)
 * @param onOpen - Handler when shortcut is triggered
 * @param options - { enabled: boolean }
 */
export function useCommandPalette(onOpen?: () => void, options: ShortcutOptions = {}): void {
  useKeyboardShortcuts(
    {
      'ctrl+k': (e: KeyboardEvent) => {
        e.preventDefault();
        onOpen?.();
      },
    },
    options
  );
}

export default useKeyboardShortcuts;
